<script setup lang="ts">
import { computed, h, type VNode } from 'vue';
import { NIcon, type DropdownOption } from 'naive-ui';
import {
  IconDelete,
  IconDocumentLock,
  IconDocumentWord,
  IconDownload,
  IconEdit,
  IconEye,
  IconFolderOpen,
  IconMore,
  IconSparkles,
  IconTime,
  IconUpload,
  IconUser,
  IconShare,
} from '@/components/design/icons';
import { PreprocessingTaskStatus } from '@/api';
import { isConversionInProgress, isPdfVaultFile } from '@/utils/fileTypeUtils';
import type { VaultItem } from '@/stores/vaultStore';
import type { FileVaultActionKey } from '@/components/file_vault/fileVaultActions';
import GaiaDropdown from '@/components/design/base/GaiaDropdown.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaSelectionBar from '@/components/design/composite/GaiaSelectionBar.vue';
import { gaiaAccent, gaiaNeutral, gaiaStatus } from '@/components/design/themes/tokens';
import { describeVaultCounts } from '@/utils/vaultSelection';
import {
  vaultActionBlock,
  vaultBulkActionBlock,
} from '@/components/file_vault/vaultActionPermissions';

const props = withDefaults(
  defineProps<{
    selectedCount: number;
    selectedFileCount: number;
    selectedFolderCount: number;
    selectedContractCount: number;
    /** Whether current user is org admin (bulk reassign) */
    isOrgAdmin: boolean;
    /** Whether at least one file or folder is selected for reassign */
    hasSelectionForReassign: boolean;
    /**
     * Whether the chat can take the selection at all. Folders have no uid to
     * reference and archives are never text-extracted, so with either in the
     * selection the button is hidden rather than shown and then refused.
     */
    canAskAi?: boolean;
    /** Resolved item when exactly one row is selected (null otherwise). */
    singleItem: VaultItem | null;
    /** The selected rows, for the permission checks: an action needs its bit on every one. */
    selectedItems: VaultItem[];
    canConvertToContract: boolean;
    /** Offer "Share" (off in the system vault, where nothing can be shared). */
    canShare: boolean;
    hasGaiaIntegration: boolean;
    /** Configured vault actions; the bar hides buttons outside this set. */
    actions?: FileVaultActionKey[];
  }>(),
  {
    actions: undefined,
    canAskAi: true,
  }
);

const hasAction = (key: FileVaultActionKey): boolean =>
  props.actions === undefined || props.actions.includes(key);

/** Why the selection can't take a bulk action; null when it can. */
function bulkBlock(action: string): string | null {
  return vaultBulkActionBlock(props.selectedItems, action);
}

/** Disables an overflow option the single item can't take, with the reason as its title. */
function blockOption(item: VaultItem, option: DropdownOption): DropdownOption {
  const reason = vaultActionBlock(item, String(option.key));
  return reason ? { ...option, disabled: true, props: { title: reason } } : option;
}

const emit = defineEmits<{
  'clear-selection': [];
  download: [];
  move: [];
  share: [];
  'ask-ai': [];
  'reassign-owner': [];
  delete: [];
  // Single-item overflow actions (only emitted when exactly one item is selected)
  rename: [];
  preview: [];
  versions: [];
  'convert-to-docx': [];
  'convert-to-contract': [];
  'export-to-gaia': [];
}>();

const reassignTooltip = computed(
  () =>
    `Reassign owner (${describeVaultCounts({
      files: props.selectedFileCount,
      contracts: props.selectedContractCount,
      folders: props.selectedFolderCount,
    })})`
);

function createDropdownIcon(icon: Record<string, unknown>): () => VNode {
  return () => h(NIcon, { size: 16 }, { default: () => h(icon) });
}

type OverflowActionKey =
  'rename' | 'preview' | 'versions' | 'convert-to-docx' | 'convert-to-contract' | 'export-to-gaia';

// Context-dependent single-item actions, mirroring the per-row "⋯" menu in
// FileVaultList (minus Download/Move/Change owner, which the bar already covers).
const overflowOptions = computed<DropdownOption[]>(() => {
  const item = props.singleItem;
  if (!item) return [];

  // Folders and contracts have no document of their own to preview, version
  // or convert — the row's own "⋯" offers them none either.
  if (item.type !== 'file') {
    return hasAction('rename')
      ? [blockOption(item, { key: 'rename', label: 'Rename', icon: createDropdownIcon(IconEdit) })]
      : [];
  }

  const options: DropdownOption[] = [];

  if (hasAction('preview')) {
    options.push({
      key: 'preview',
      label: 'Preview',
      icon: createDropdownIcon(IconEye),
      disabled: item.previewStatus !== PreprocessingTaskStatus.DONE,
    });
  }

  if (hasAction('versions')) {
    options.push({
      key: 'versions',
      label: 'Version history',
      icon: createDropdownIcon(IconTime),
    });
  }

  if (hasAction('convert-to-docx') && isPdfVaultFile(item)) {
    const converting = isConversionInProgress(item);
    options.push({
      key: 'convert-to-docx',
      label: converting ? 'Conversion in progress' : 'Convert to DOCX',
      icon: createDropdownIcon(IconDocumentWord),
      disabled: converting,
    });
  }

  if (hasAction('convert-to-contract') && props.canConvertToContract) {
    options.push({
      key: 'convert-to-contract',
      label: 'Convert to contract',
      icon: createDropdownIcon(IconDocumentLock),
    });
  }

  if (hasAction('export-to-gaia') && props.hasGaiaIntegration) {
    options.push({
      key: 'export-to-gaia',
      label: 'Export to Gaia CLM Section',
      icon: createDropdownIcon(IconUpload),
    });
  }

  if (hasAction('rename')) {
    options.push({ key: 'rename', label: 'Rename', icon: createDropdownIcon(IconEdit) });
  }

  return options.map(option => blockOption(item, option));
});

function handleOverflowSelect(key: string | number): void {
  switch (String(key) as OverflowActionKey) {
    case 'rename':
      emit('rename');
      return;
    case 'preview':
      emit('preview');
      return;
    case 'versions':
      emit('versions');
      return;
    case 'convert-to-docx':
      emit('convert-to-docx');
      return;
    case 'convert-to-contract':
      emit('convert-to-contract');
      return;
    case 'export-to-gaia':
      emit('export-to-gaia');
      return;
  }
}

const actionBorder = gaiaNeutral.border;
const actionBorderHover = gaiaNeutral.borderHoverSoft;
const actionSurface = gaiaNeutral.surface;
const actionColor = gaiaNeutral.text;
const askColor = gaiaAccent.soft;
const askRing = gaiaAccent.softRing;
const dangerColor = gaiaStatus.alert;
const dangerBorder = gaiaStatus.alertBorder;
const dangerSurface = gaiaStatus.alertSurface;
</script>

<template>
  <!-- The bulk toolbar from the File Vault mock. GaiaSelectionBar supplies the
       frame, the leading dismiss and the count; every action here is one of its
       round 30px buttons, in the mock's order: read it, take it, move it, sort
       it, hand it over, delete it. -->
  <gaia-selection-bar :label="`${props.selectedCount} selected`" @clear="emit('clear-selection')">
    <gaia-tooltip v-if="hasAction('ask-ai') && props.canAskAi">
      <template #trigger>
        <gaia-icon-button :dimension="30" circle class="action ask" @click="emit('ask-ai')">
          <n-icon :component="IconSparkles" :size="13" />
        </gaia-icon-button>
      </template>
      Ask AI about the selection
    </gaia-tooltip>

    <gaia-tooltip v-if="hasAction('download')">
      <template #trigger>
        <!-- A disabled button swallows hover; the span keeps the reason showing. -->
        <span>
          <gaia-icon-button
            :dimension="30"
            circle
            class="action"
            :disabled="bulkBlock('download') !== null"
            @click="emit('download')"
          >
            <n-icon :component="IconDownload" :size="13" />
          </gaia-icon-button>
        </span>
      </template>
      {{ bulkBlock('download') ?? 'Download' }}
    </gaia-tooltip>

    <gaia-tooltip v-if="hasAction('move')">
      <template #trigger>
        <!-- A disabled button swallows hover; the span keeps the reason showing. -->
        <span>
          <gaia-icon-button
            :dimension="30"
            circle
            class="action"
            :disabled="bulkBlock('move') !== null"
            @click="emit('move')"
          >
            <n-icon :component="IconFolderOpen" :size="13" />
          </gaia-icon-button>
        </span>
      </template>
      {{ bulkBlock('move') ?? 'Move to…' }}
    </gaia-tooltip>

    <gaia-tooltip v-if="hasAction('share') && props.canShare">
      <template #trigger>
        <!-- A disabled button swallows hover; the span keeps the reason showing. -->
        <span>
          <gaia-icon-button
            :dimension="30"
            circle
            class="action"
            :disabled="bulkBlock('share') !== null"
            @click="emit('share')"
          >
            <n-icon :component="IconShare" :size="13" />
          </gaia-icon-button>
        </span>
      </template>
      {{ bulkBlock('share') ?? 'Share with users and roles' }}
    </gaia-tooltip>

    <gaia-tooltip
      v-if="hasAction('change-owner') && props.isOrgAdmin && props.hasSelectionForReassign"
    >
      <template #trigger>
        <gaia-icon-button :dimension="30" circle class="action" @click="emit('reassign-owner')">
          <n-icon :component="IconUser" :size="13" />
        </gaia-icon-button>
      </template>
      {{ reassignTooltip }}
    </gaia-tooltip>

    <gaia-tooltip v-if="hasAction('delete')">
      <template #trigger>
        <!-- A disabled button swallows hover; the span keeps the reason showing. -->
        <span>
          <gaia-icon-button
            :dimension="30"
            circle
            class="action danger"
            :disabled="bulkBlock('delete') !== null"
            @click="emit('delete')"
          >
            <n-icon :component="IconDelete" :size="13" />
          </gaia-icon-button>
        </span>
      </template>
      {{ bulkBlock('delete') ?? 'Delete' }}
    </gaia-tooltip>

    <!-- Everything a single selected row can do that the bar has no room for —
         the same list as its "⋯" menu, minus what the bar already shows. -->
    <gaia-dropdown
      v-if="props.selectedCount === 1 && props.singleItem && overflowOptions.length > 0"
      :options="overflowOptions"
      placement="bottom-end"
      @select="handleOverflowSelect"
    >
      <gaia-icon-button :dimension="30" circle class="action" title="More actions">
        <n-icon :component="IconMore" :size="14" />
      </gaia-icon-button>
    </gaia-dropdown>
  </gaia-selection-bar>
</template>

<style scoped>
/* Round, bordered and on white — these sit on the bar's own soft surface, so a
   quaternary button would dissolve into it. */
.action {
  border: 1px solid v-bind(actionBorder);
  background: v-bind(actionSurface);
  color: v-bind(actionColor);
}

.action:hover {
  border-color: v-bind(actionBorderHover);
}

/* Ask AI is the one generative action in the row, so it carries the accent —
   the soft violet, not the primary, since it is a control rather than the
   page's main action. */
.action.ask {
  color: v-bind(askColor);
}

.action.ask:hover {
  border-color: v-bind(askRing);
}

.action.danger {
  border-color: v-bind(dangerBorder);
  color: v-bind(dangerColor);
}

.action.danger:hover {
  border-color: v-bind(dangerColor);
  background: v-bind(dangerSurface);
}
</style>
